import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Hash, Building2, UserCircle, FileWarning, Loader2, type LucideIcon } from 'lucide-react';
import { watchlistApi } from '@/api/watchlist';
import { WatchlistSubNav } from '@/components/watchlist/SubNav';
import { wl, classificationColor } from '@/components/watchlist/theme';

function StatCard({ icon: Icon, label, value, color }: { icon: LucideIcon; label: string; value: number; color: string }) {
  return (
    <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '16px 18px' }} className="flex items-center gap-4">
      <div style={{ width: 42, height: 42, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}30` }} className="flex items-center justify-center flex-shrink-0">
        <Icon size={19} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div style={{ color: wl.text, fontWeight: 800, fontSize: 24, lineHeight: 1.1 }}>{value.toLocaleString()}</div>
        <div style={{ color: wl.textMuted, fontSize: 10.5, marginTop: 3, letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      </div>
    </div>
  );
}

function FunnelStage({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8, padding: '12px 14px', flex: 1, minWidth: 110 }}>
      <p style={{ color: wl.textMuted, fontSize: 10.5 }}>{label}</p>
      <p style={{ color, fontWeight: 800, fontSize: 20, marginTop: 4 }}>{value.toLocaleString()}</p>
    </div>
  );
}

function relativeTime(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime();
  const m = Math.floor(delta / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Best-effort link when a hit predates this session's post_url fix (older
// hits were recorded before record_hit started persisting it) — only for
// platforms where a bare post id reliably resolves to a real URL on its
// own, with no username/slug needed. Every other platform just shows no
// link rather than a guessed, possibly-wrong one.
function reconstructPostUrl(platform: string, postId: string): string | null {
  const p = platform.toLowerCase();
  if (p === 'twitter' || p === 'x') return `https://x.com/i/status/${postId}`;
  if (p === 'youtube') return `https://www.youtube.com/watch?v=${postId}`;
  return null;
}

const CLASSIFICATION_ORDER = ['official', 'declared_fan', 'impersonation', 'scam_impersonation'];
const PLATFORM_COLORS = [wl.green, wl.info, wl.medium, wl.high, wl.critical, wl.greenDim];

export default function WatchlistDashboard() {
  const keywordsQuery = useQuery({ queryKey: ['watchlist-keyword-sets'], queryFn: () => watchlistApi.listKeywordSets(), staleTime: 30_000 });
  const brandsQuery = useQuery({ queryKey: ['watchlist-brand-profiles'], queryFn: () => watchlistApi.listBrandProfiles(), staleTime: 30_000 });
  const vipsQuery = useQuery({ queryKey: ['watchlist-vip-profiles'], queryFn: () => watchlistApi.listVipProfiles(), staleTime: 30_000 });
  const casesQuery = useQuery({ queryKey: ['watchlist-takedown-cases'], queryFn: () => watchlistApi.listTakedownCases(), staleTime: 30_000 });
  // Fetched wider than the list actually displays so the analytics panel
  // (by-platform / top-terms) reflects a real sample, not just the visible
  // handful of rows.
  const hitsQuery = useQuery({ queryKey: ['watchlist-recent-hits'], queryFn: () => watchlistApi.listAllHits(50), staleTime: 15_000 });
  const statsQuery = useQuery({ queryKey: ['watchlist-overview-stats'], queryFn: () => watchlistApi.getOverviewStats(), staleTime: 30_000 });

  const isLoading = keywordsQuery.isLoading || brandsQuery.isLoading || vipsQuery.isLoading || casesQuery.isLoading;
  const openCases = (casesQuery.data?.cases ?? []).filter((c) => !['removed', 'rejected'].includes(c.status)).length;
  const stats = statsQuery.data;

  const classificationTotal = stats ? Object.values(stats.classification_breakdown).reduce((a, b) => a + b, 0) : 0;

  const keywordSetName = (id: string) => keywordsQuery.data?.keyword_sets.find((k) => k.id === id)?.name ?? 'Unknown set';

  const hits = hitsQuery.data?.hits ?? [];
  const hitAnalytics = useMemo(() => {
    const byPlatform = new Map<string, number>();
    const byTerm = new Map<string, number>();
    hits.forEach((h) => {
      byPlatform.set(h.platform, (byPlatform.get(h.platform) ?? 0) + 1);
      h.matched_terms.forEach((t) => byTerm.set(t, (byTerm.get(t) ?? 0) + 1));
    });
    return {
      platforms: Array.from(byPlatform.entries()).sort((a, b) => b[1] - a[1]),
      terms: Array.from(byTerm.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8),
      sampleSize: hits.length,
    };
  }, [hits]);

  return (
    <div style={{ background: wl.bg, minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WatchlistSubNav />

      <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10 }} className="px-7 py-5 mb-6">
        <h1 style={{ color: wl.text, fontWeight: 800, fontSize: 18, letterSpacing: '0.02em' }}>
          Watchlist &amp; Impersonation Protection
        </h1>
        <p style={{ color: wl.textMuted, fontSize: 12.5, marginTop: 3 }}>
          Multi-language keyword monitoring · brand &amp; VIP impersonation detection · takedown tracking
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin" size={24} style={{ color: wl.green }} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Hash} label="KEYWORD SETS" value={keywordsQuery.data?.total ?? 0} color={wl.green} />
            <StatCard icon={Building2} label="BRAND PROFILES" value={brandsQuery.data?.total ?? 0} color={wl.medium} />
            <StatCard icon={UserCircle} label="VIP PROFILES" value={vipsQuery.data?.total ?? 0} color={wl.info} />
            <StatCard icon={FileWarning} label="OPEN TAKEDOWN CASES" value={openCases} color={wl.critical} />
          </div>

          {stats && (
            <div className="mb-6">
              <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 10 }}>DETECTION PIPELINE · ALL TIME</p>
              <div className="flex gap-3 flex-wrap">
                <FunnelStage label="Posts scanned" value={stats.funnel.posts_scanned} color={wl.textMuted} />
                <FunnelStage label="Keyword hits" value={stats.funnel.keyword_hits} color={wl.green} />
                <FunnelStage label="Candidates" value={stats.funnel.candidates} color={wl.info} />
                <FunnelStage label="Impersonation/scam" value={stats.funnel.impersonation_or_scam} color={wl.critical} />
                <FunnelStage label="Takedowns open" value={stats.funnel.takedowns_open} color={wl.high} />
                <FunnelStage label="Removed (30d)" value={stats.funnel.removed_30d} color={wl.greenDim} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 mb-6">
            <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }}>
              <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 12 }}>KEYWORD HITS · 14 DAYS</p>
              {stats && stats.hits_by_day_14d.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={stats.hits_by_day_14d}>
                    <defs>
                      <linearGradient id="wlHitsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={wl.green} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={wl.green} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={wl.borderSoft} vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: wl.textFaint, fontSize: 10 }} axisLine={{ stroke: wl.border }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fill: wl.textFaint, fontSize: 10 }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={{ background: wl.bg, border: `1px solid ${wl.border}`, borderRadius: 8, fontSize: 11 }} labelStyle={{ color: wl.text }} />
                    <Area type="monotone" dataKey="count" stroke={wl.green} fill="url(#wlHitsGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p style={{ color: wl.textFaint, fontSize: 12.5 }}>No keyword hits in the last 14 days.</p>
              )}
            </div>

            <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }}>
              <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 12 }}>CANDIDATE CLASSIFICATION</p>
              {stats && classificationTotal > 0 ? (
                <div className="flex flex-col gap-2.5">
                  {CLASSIFICATION_ORDER.map((key) => {
                    const count = stats.classification_breakdown[key] ?? 0;
                    const pct = (count / classificationTotal) * 100;
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span style={{ color: wl.textMuted, fontSize: 11, textTransform: 'capitalize' }}>{key.replace('_', ' ')}</span>
                          <span style={{ color: wl.text, fontSize: 11 }}>{count}</span>
                        </div>
                        <div style={{ height: 5, background: wl.borderSoft, borderRadius: 3 }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: classificationColor(key), borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: wl.textFaint, fontSize: 12 }}>No candidates scored yet.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
            <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '20px 22px' }}>
              <div className="flex items-center justify-between mb-3">
                <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700 }}>RECENT KEYWORD HITS</p>
                {hits.length > 0 && <span style={{ color: wl.textFaint, fontSize: 10.5 }}>last {hits.length}</span>}
              </div>
              {hitsQuery.isLoading ? (
                <Loader2 className="animate-spin" size={18} style={{ color: wl.green }} />
              ) : hits.length === 0 ? (
                <p style={{ color: wl.textFaint, fontSize: 12.5 }}>No detections yet.</p>
              ) : (
                <div className="flex flex-col gap-2.5" style={{ maxHeight: 520, overflowY: 'auto' }}>
                  {hits.map((hit) => {
                    const link = hit.post_url ?? reconstructPostUrl(hit.platform, hit.post_id);
                    return (
                      <div key={hit.id} className="py-2.5 px-3" style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, borderRadius: 8 }}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                              <span style={{ color: wl.green, fontSize: 10, fontWeight: 700, background: wl.greenWash, padding: '1px 7px', borderRadius: 6 }}>
                                {keywordSetName(hit.keyword_set_id)}
                              </span>
                              <span style={{ color: wl.textMuted, fontSize: 10.5, textTransform: 'capitalize' }}>{hit.platform}</span>
                              <span style={{ color: wl.textFaint, fontSize: 10 }}>·</span>
                              <span style={{ color: wl.textFaint, fontSize: 10.5 }} title={new Date(hit.detected_at).toLocaleString()}>
                                {relativeTime(hit.detected_at)}
                              </span>
                            </div>
                            <p style={{ color: wl.text, fontSize: 12.5, lineHeight: 1.5 }}>{hit.post_text_snippet}</p>
                            <div className="flex gap-1.5 mt-1.5 flex-wrap">
                              {hit.matched_terms.map((t) => (
                                <span key={t} style={{ background: wl.greenWash, color: wl.green, fontSize: 10, padding: '1px 6px', borderRadius: 6 }}>{t}</span>
                              ))}
                            </div>
                          </div>
                          {link ? (
                            <a href={link} target="_blank" rel="noreferrer" style={{ color: wl.green, fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>
                              View post →
                            </a>
                          ) : (
                            <span style={{ color: wl.textFaint, fontSize: 10.5, flexShrink: 0, whiteSpace: 'nowrap' }}>no link</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ background: wl.surface, border: `1px solid ${wl.border}`, borderRadius: 10, padding: '18px 20px' }}>
              <p style={{ color: wl.textFaint, fontSize: 10.5, letterSpacing: '0.08em', fontWeight: 700, marginBottom: 12 }}>HIT ANALYTICS</p>
              {hitAnalytics.sampleSize === 0 ? (
                <p style={{ color: wl.textFaint, fontSize: 12 }}>No hits to analyze yet.</p>
              ) : (
                <>
                  <p style={{ color: wl.textFaint, fontSize: 10, marginBottom: 10 }}>across the last {hitAnalytics.sampleSize} hits</p>
                  <p style={{ color: wl.textMuted, fontSize: 10.5, letterSpacing: '0.04em', fontWeight: 700, marginBottom: 6 }}>BY PLATFORM</p>
                  <div className="flex flex-col gap-2 mb-4">
                    {hitAnalytics.platforms.map(([platform, count], i) => (
                      <div key={platform}>
                        <div className="flex items-center justify-between mb-1">
                          <span style={{ color: wl.text, fontSize: 11, textTransform: 'capitalize' }}>{platform}</span>
                          <span style={{ color: wl.textMuted, fontSize: 10.5 }}>{count}</span>
                        </div>
                        <div style={{ height: 4, background: wl.borderSoft, borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${(count / hitAnalytics.sampleSize) * 100}%`, background: PLATFORM_COLORS[i % PLATFORM_COLORS.length], borderRadius: 2 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ color: wl.textMuted, fontSize: 10.5, letterSpacing: '0.04em', fontWeight: 700, marginBottom: 6 }}>TOP MATCHED TERMS</p>
                  <div className="flex flex-wrap gap-1.5">
                    {hitAnalytics.terms.map(([term, count]) => (
                      <span key={term} style={{ background: wl.bg, border: `1px solid ${wl.borderSoft}`, color: wl.text, fontSize: 10.5, padding: '2px 8px', borderRadius: 6 }}>
                        {term} <span style={{ color: wl.textFaint }}>· {count}</span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
